<template>
  <div class="resources-page">
    <Header></Header>
    <Nav></Nav>

    <div class="hero">
      <div class="hero-content">
        <h1>{{ $t("resources.heroTitle") }}</h1>
        <p>{{ $t("resources.heroSubtitle") }}</p>
      </div>
    </div>

    <div class="resources-container">
      <div class="resources-intro">
        <h2>{{ $t("resources.categoryTitle") }}</h2>
        <p>{{ $t("resources.categoryIntro") }}</p>
      </div>

      <div v-if="loadingCategories" class="state-tip">
        {{ $t("notice.loading") }}
      </div>
      <div v-else-if="categoryLoadFailed" class="state-tip">
        {{ $t("resources.loadFailed") }}
      </div>
      <template v-else>
        <!-- 资源分类标签 -->
        <div class="resources-tabs">
          <div
            v-for="category in categories"
            :key="category.id"
            class="resources-tab"
            :class="{ active: activeCategoryId === category.id }"
            @click="selectCategory(category)"
          >
            {{ localizedCategoryName(category) }}
          </div>
        </div>

        <div class="resources-content">
          <div v-if="loadingResources" class="state-tip">
            {{ $t("notice.loading") }}
          </div>
          <div v-else-if="resourceLoadFailed" class="state-tip">
            {{ $t("resources.loadFailed") }}
          </div>
          <div v-else-if="resources.length === 0" class="state-tip">
            {{ $t("resources.empty") }}
          </div>
          <div v-else class="resource-list">
            <div
              class="resource-item"
              v-for="item in resources"
              :key="item.id"
            >
              <div class="item-image" @click="showResourceDetail(item)">
                <img
                  v-if="item.thumbnailUrl"
                  :src="item.thumbnailUrl"
                  :alt="localizedResourceName(item)"
                />
                <span v-else>{{ localizedResourceName(item) }}</span>
              </div>
              <div class="item-content">
                <div class="resource-title" @click="showResourceDetail(item)">
                  {{ localizedResourceName(item) }}
                </div>
                <div class="resource-desc">
                  {{ localizedResourceSummary(item) }}
                </div>
                <a
                  href="javascript:void(0)"
                  class="view-detail"
                  @click="showResourceDetail(item)"
                >
                  {{ $t("resources.viewDetail") }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- 资源图文详情弹窗 -->
      <div v-if="detailVisible" class="resource-modal">
        <div class="resource-modal-content">
          <div class="resource-modal-header">
            <h3>{{ localizedResourceName(currentResource) }}</h3>
            <span class="close-btn" @click="closeResourceDetail">×</span>
          </div>
          <div class="resource-modal-body">
            <p class="detail-summary">{{ localizedResourceDescription(currentResource) }}</p>
            <div class="gallery-list">
              <div
                class="gallery-item"
                v-for="imageUrl in currentImageUrls"
                :key="imageUrl"
                @click="showImagePreview(imageUrl)"
              >
                <img :src="imageUrl" :alt="localizedResourceName(currentResource)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 图片预览弹窗 -->
      <div
        v-if="previewVisible"
        class="image-preview-modal"
        @click.self="closeImagePreview"
      >
        <div class="image-preview-content">
          <span class="close-btn image-preview-close" @click="closeImagePreview">×</span>
          <img
            :src="previewImageUrl"
            :alt="localizedResourceName(currentResource)"
          />
        </div>
      </div>
    </div>

    <Footer></Footer>
  </div>
</template>

<script>
import Header from "../../components/head.vue";
import Nav from "../../components/nav.vue";
import Footer from "../../components/footer.vue";
import { listResourceCategories, listResources } from "../../api/resources";

export default {
  name: "ResourcesPage",
  components: { Header, Nav, Footer },
  data() {
    return {
      categories: [],
      resources: [],
      activeCategoryId: null,
      detailVisible: false,
      previewVisible: false,
      currentResource: {},
      previewImageUrl: "",
      loadingCategories: false,
      loadingResources: false,
      categoryLoadFailed: false,
      resourceLoadFailed: false,
    };
  },
  computed: {
    currentImageUrls() {
      if (!this.currentResource.imageUrls) {
        return [];
      }

      return this.currentResource.imageUrls
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url);
    },
  },
  mounted() {
    this.getCategoryList();
  },
  methods: {
    localizedValue(target, zhField, enField) {
      if (!target) {
        return "";
      }

      if (this.$i18n.locale === "en") {
        return target[enField] || target[zhField] || "";
      }

      return target[zhField] || target[enField] || "";
    },
    localizedCategoryName(category) {
      return this.localizedValue(category, "categoryName", "categoryNameEn");
    },
    localizedResourceName(resource) {
      return this.localizedValue(resource, "resourceName", "resourceNameEn");
    },
    localizedResourceSummary(resource) {
      return this.localizedValue(resource, "resourceSummary", "resourceSummaryEn");
    },
    localizedResourceDescription(resource) {
      return this.localizedValue(resource, "description", "descriptionEn")
        || this.localizedResourceSummary(resource);
    },
    async getCategoryList() {
      this.loadingCategories = true;
      this.categoryLoadFailed = false;
      try {
        const res = await listResourceCategories({ status: 1 });
        if (res.code === 0) {
          this.categories = res.rows || [];
          if (this.categories.length > 0) {
            this.selectCategory(this.categories[0]);
          }
        } else {
          this.categoryLoadFailed = true;
        }
      } catch (e) {
        this.categoryLoadFailed = true;
      }
      this.loadingCategories = false;
    },
    async getResourceList() {
      if (!this.activeCategoryId) {
        this.resources = [];
        return;
      }

      this.loadingResources = true;
      this.resourceLoadFailed = false;
      try {
        const res = await listResources({
          categoryId: this.activeCategoryId,
          status: 1,
        });
        if (res.code === 0) {
          this.resources = res.rows || [];
        } else {
          this.resourceLoadFailed = true;
        }
      } catch (e) {
        this.resourceLoadFailed = true;
      }
      this.loadingResources = false;
    },
    selectCategory(category) {
      this.activeCategoryId = category.id;
      this.getResourceList();
    },
    showResourceDetail(item) {
      this.currentResource = item;
      this.detailVisible = true;
    },
    closeResourceDetail() {
      this.detailVisible = false;
      this.closeImagePreview();
      this.currentResource = {};
    },
    showImagePreview(imageUrl) {
      this.previewImageUrl = imageUrl;
      this.previewVisible = true;
    },
    closeImagePreview() {
      this.previewVisible = false;
      this.previewImageUrl = "";
    },
  },
};
</script>

<style scoped>
.hero {
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="400"><rect width="1200" height="400" fill="%23004080"/><circle cx="200" cy="150" r="50" fill="%23FFD700" opacity="0.3"/><circle cx="800" cy="250" r="80" fill="%23FFD700" opacity="0.2"/><rect x="400" y="100" width="300" height="200" fill="%23FFD700" opacity="0.1"/></svg>');
  background-size: cover;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #fff;
}

.hero-content h1 {
  font-size: 2.5em;
  margin-bottom: 20px;
}

.hero-content p {
  font-size: 1.1em;
}

.resources-container {
  max-width: 1200px;
  margin: 60px auto;
  padding: 0 20px;
}

.resources-intro {
  text-align: center;
}

.resources-intro h2 {
  color: #003366;
  font-size: 1.8em;
  margin-bottom: 20px;
}

.resources-intro p {
  max-width: 760px;
  margin: 0 auto 30px;
  color: #555;
  line-height: 1.8;
}

.resources-tabs {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.resources-tab {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
}

.resources-tab.active {
  color: #004080;
  border-bottom: 2px solid #004080;
  font-weight: bold;
}

.state-tip {
  padding: 30px 0;
  color: #999;
  text-align: center;
}

.resource-item {
  display: flex;
  margin-bottom: 20px;
  padding: 10px;
  align-items: flex-start;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.resource-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.item-image {
  width: 160px;
  height: 120px;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  background-color: #e0e0e0;
  overflow: hidden;
  cursor: pointer;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.item-content {
  flex: 1;
}

.resource-title {
  margin-bottom: 10px;
  color: #003366;
  font-size: 1.1em;
  font-weight: bold;
  cursor: pointer;
  transition: color 0.3s;
}

.resource-title:hover {
  color: #0066cc;
  text-decoration: underline;
}

.resource-desc {
  margin-bottom: 12px;
  color: #555;
  line-height: 1.8;
}

.view-detail {
  color: #004080;
  font-weight: bold;
  text-decoration: none;
}

.view-detail:hover {
  text-decoration: underline;
}

.resource-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  background: rgba(0, 0, 0, 0.6);
}

.resource-modal-content {
  width: 80%;
  max-width: 1000px;
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.resource-modal-header {
  display: flex;
  padding: 12px 20px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ddd;
  background: #f5f5f5;
}

.resource-modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  font-size: 24px;
  cursor: pointer;
}

.resource-modal-body {
  padding: 20px;
  overflow-y: auto;
}

.detail-summary {
  margin-top: 0;
  margin-bottom: 20px;
  color: #555;
  line-height: 1.8;
}

.gallery-list {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
}

.gallery-item img {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 6px;
  object-fit: cover;
  background: #f4f8fc;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.gallery-item img:hover {
  transform: scale(1.02);
}

.image-preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.78);
  box-sizing: border-box;
}

.image-preview-content {
  position: relative;
  max-width: 92vw;
  max-height: 92vh;
}

.image-preview-content img {
  display: block;
  max-width: 100%;
  max-height: 92vh;
  border-radius: 6px;
  object-fit: contain;
  background: #fff;
}

.image-preview-close {
  position: absolute;
  top: -16px;
  right: -16px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #333;
  line-height: 32px;
  text-align: center;
  background: #fff;
}

@media (max-width: 768px) {
  .resources-tabs {
    flex-wrap: wrap;
  }

  .resources-tab {
    padding: 10px 18px;
    font-size: 0.95rem;
  }

  .resource-item {
    flex-direction: column;
  }

  .item-image {
    width: 100%;
    height: 180px;
    margin-right: 0;
    margin-bottom: 14px;
  }

  .resource-modal-content {
    width: 92%;
  }
}
</style>
